import {IUserAddress, IUserEducationItem} from '../redux/modules/scheduler/reducer';
import {NO_DEGREE} from '../data/dataEducation';
import {INSTITUTION} from './config';

export const MAX_LENGTH_JOB_DESCRIPTION = 500;

export const validateRequiredField = (fieldName: string, fieldValue: string): string => {
  return !fieldValue ? `${fieldName} is required` : '';
};

export const validateFirstName = (firstName: string): string => {
  if (!firstName) {
    return 'Please enter a first name';
  }

  if (firstName.length > 100) {
    return 'Please enter a shorter first name';
  }

  return '';
};

export const validateLastName = (lastName: string): string => {
  if (!lastName) {
    return 'Please enter a last name';
  }

  if (lastName.length > 100) {
    return 'Please enter a shorter last name';
  }

  return '';
};

export const validateEmail = (email: string): string => {
  if (!email || !/@/.test(email)) {
    return 'Please enter a valid email';
  }

  if (INSTITUTION) {
    const {title, emailPattern} = INSTITUTION;

    return new RegExp(emailPattern).test(email)
      ? ''
      : `Please use an email that's associated with institution ${title}`;
  }

  return '';
};

export const validatePhone = (phone: string): string => {
  const errorMessage = 'Please enter a valid phone';

  if (!phone) {
    return errorMessage;
  }

  if (phone.replace(/\D/g, '').length < 6 || /[a-z]/i.test(phone)) {
    return errorMessage;
  }

  return '';
};

export const validateCurrentCity = (address: IUserAddress): string => {
  const city = address ? address.autocomplete : address;
  return city ? '' : 'Please select your city';
};

export const validateSkills = (skills: Array<any>) => {
  if (skills.length === 0) {
    return 'Please select at least one skill';
  }

  return '';
};

export const validateRoles = (roles: Array<any>): string => {
  if (roles.length === 0) {
    return 'Please select at least one role';
  }

  return '';
};

export const validateExperience = (experience: string): string => {
  if (!experience || experience.length === 0) {
    return 'Please select your experience';
  }

  return '';
};

export const validateLanguages = (languages: Array<any>): string => {
  if (languages.length === 0) {
    return 'Please select at least one language';
  }

  return '';
};

export const validateResumeCity = (city: string) => {
  return !city ? 'Please select your city' : '';
};

export const validateWorkAvailability = (value: string) => {
  return value ? '' : 'Please select your availability';
};

export const validateWorkAuthType = (value: any) => {
  return value ? '' : 'Please select your work authorization';
};

export const validateWorkType = (value: any) => {
  return value ? '' : `Please specify the type of work you're interested in`;
};

export const validateSwitchState = (value: any) => (
  value === null
    ? `Please select one of the options`
    : ''
);

export const validatePassword = (password: string): string => {
  if (!password) {
    return 'Please enter password';
  }

  if (password.length < 6) {
    return 'Please enter a password longer than 6 characters';
  }

  return '';
};

export const validateRating = (rating: number): string => {
  if (!rating) {
    return 'Please select rating';
  }

  return '';
};

export const validateDecision = (decision: string): string => {
  return (!decision || ['Yes', 'No'].indexOf(decision) === -1)
    ? 'Please let us know about your decision'
    : null;
};

export const validateReason = (reason: string) => {
  return !reason ? 'Please specify the reason for your decision' : null;
};

export const validateUserSlot = (slotId: string): string => {
  return !slotId ? 'Please select a slot' : '';
};

export const validatePasswordMatch = (newPassword, confirmNewPassword): string => {
  return newPassword !== confirmNewPassword ? 'Passwords must match' : '';
};

export const validateEducation = (educations: Array<IUserEducationItem>): IUserEducationItem | string => {
  const defaultEducation = {status: '', degree: '', studyArea: ''};

  if (!educations) {
    return {status: 'Please select your education', degree: '', studyArea: ''};
  }

  const validationResult =  educations.reduce((result: any, educationItem: IUserEducationItem) => {
    if (educationItem.status && educationItem.status === NO_DEGREE ) {return '';}

    if (!educationItem.status || !educationItem.degree || !educationItem.studyArea) {
      result = {
        status: !result.status && !educationItem.status && 'Please select status',
        degree: !result.degree && !educationItem.degree && 'Please select degree',
        studyArea: !result.studyArea && !educationItem.studyArea && 'Please select study area',
      };
    }

    return result;
  }, defaultEducation);

  return validationResult === defaultEducation ? '' : validationResult;
};

export const validateJobDescription = (description: string) => {
  if (!description) return 'Please share a short description';
  return description.length > MAX_LENGTH_JOB_DESCRIPTION ? 'Character limit exceeded' : null;
};

export const validateCompanyName = (companyName: string) => {
  if (!companyName) {
    return 'Please enter a company name';
  }

  return '';
};

export const validateHiringPlans = (hiringPlans: string) => {
  if (!hiringPlans) {
    return 'Please select an option';
  }

  return '';
};

export const validateRole = (role: string) => {
  if (!role) {
    return 'Please choose a role';
  }

  return '';
};

export const validateCity = (city: string) => {
  if (!city) {
    return 'Please enter a city';
  }

  return '';
};

export const getValidator = (fieldName: string, value: any) => {
  switch (fieldName) {
    case 'address':
      return validateCurrentCity.bind(value);
    case 'experience':
      return validateExperience.bind(value);
    case 'education':
      return validateEducation.bind(value);
    case 'roles':
      return validateRoles.bind(value);
    case 'languages':
      return validateLanguages.bind(value);
    case 'selfPrep':
      return validateRating.bind(value);
    case 'firstName':
      return validateFirstName.bind(value);
    case 'lastName':
      return validateLastName.bind(value);
    case 'email':
      return validateEmail.bind(value);
    case 'password':
      return validatePassword.bind(value);
    case 'decision':
      return validateDecision.bind(value);
    case 'reason':
      return validateReason.bind(value);
    case 'workAvailability':
      return validateWorkAvailability.bind(value);
    case 'workType':
      return validateWorkType.bind(value);
    case 'workAuthType':
      return validateWorkAuthType.bind(value);
    case 'bayArea':
    case 'newYork':
    case 'remote':
      return validateSwitchState.bind(value);
    case 'jobUsername':
      return validateFirstName.bind(value);
    case 'jobEmail':
      return validateEmail.bind(value);
    case 'jobDescription':
      return validateJobDescription.bind(value);
    case 'companyName':
      return validateCompanyName.bind(value);
    case 'hiringPlans':
      return validateHiringPlans.bind(value);
    case 'role':
      return validateRole.bind(value);
    case 'city':
      return validateCity.bind(value);
    default:
      return null;
  }
};
